import React, { useEffect } from 'react';
import { Typography, Tooltip, Button } from '@material-ui/core';
import { t } from 'ttag';
import moment from 'moment-timezone';
import { useStoreState } from '../../../models/RootStore';
import UnsnoozeIcon from './UnsnoozeIcon';
import useStyles from '../AlertInfo.style';
import { useConfirmation } from '@coolremote/shared';

/**
 * Props for the SnoozeStatusBanner component.
 * @interface SnoozeStatusBannerProps
 * @property {string} username - The name of the user who initiated the snooze.
 * @property {string} scope - The scope of the snooze (e.g., 'system' or 'unit').
 * @property {string} endTime - The formatted end time when the snooze expires.
 * @property {(localOnly?: boolean) => void} onCancel - Callback function triggered when the user cancels the snooze.
 * @property {string} [snoozeNote] - Optional note added by the user when snoozing. Displayed as a tooltip on hover.
 * @property {string} [timezone] - Optional timezone to parse endTime correctly.
 */
interface SnoozeStatusBannerProps {
  username: string;
  scope: string;
  endTime: string;
  onCancel: (localOnly?: boolean) => void;
  snoozeNote?: string;
  isExpired?: boolean;
}

/**
 * A banner component that displays the current snooze status of an alert.
 * Shows who snoozed it, the scope, the end time, and provides an option to cancel.
 * 
 * @component
 * @example
 * <SnoozeStatusBanner 
 *   username="John Doe" 
 *   scope="unit" 
 *   endTime="12/05/2026 14:00" 
 *   onCancel={() => handleCancel()} 
 * />
 */
const SnoozeStatusBanner: React.FC<SnoozeStatusBannerProps> = ({
  username,
  scope,
  endTime,
  onCancel,
  snoozeNote,
  isExpired = false,
}) => {
  const classes: any = useStyles();

  const confirm = useConfirmation();

  const handleUnsnoozeClick = async () => {
    const result = await confirm({
      title: t`Cancel Snooze`,
      bodyMessage: t`Are you sure you want to cancel the snooze for this alert?`,
      confirmText: t`Ok`,
      cancelText: t`Cancel`,
    })
    result && onCancel()
  }

  const renderMessageContent = () => {
    if (isExpired) {
      return (
        <div className={classes.snoozeBannerTextExpired}>
          <div>{t`Snoozed alert (for ${scope}) Expired: ${endTime}.`}</div>
          <div>{t`By ${username}`}</div>
        </div>
      )
    }

    return (
      <>
        <div>{t`Alerts Snoozed for ${scope}`}</div>
        <div>{t`End:`} {endTime}. {t`By`} {username}</div>
      </>
    )
  }

  return (
    <Tooltip
      title={snoozeNote || ""}
      disableHoverListener={!snoozeNote}
      classes={{ tooltip: classes.snoozeBannerTooltip }}
    >
      <div className={classes.snoozeBanner}>

        <Typography className={classes.snoozeBannerText}>
          <UnsnoozeIcon />
          <div>
            {renderMessageContent()}
          </div>
        </Typography>
        {!isExpired && (
          <Button className={classes.cancelSnoozeButton} onClick={handleUnsnoozeClick} variant="contained" disableElevation size="small" title={t`Cancel Snooze`}>
            {t`Unsnooze`}
          </Button>
        )}

      </div>
    </Tooltip>
  );
};

export default SnoozeStatusBanner;
